import { useState, useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  ExSideDrawer,
  ExInput,
  ExSelect,
  ExButton,
  ExMenuItem,
  ExCheckbox,
  ExCard,
  ExBadge,
  ExTooltip,
  ExAlertBanner,
  ExLoader,
  ExCombobox,
  ExEmptyState,
  ButtonFlavor,
  ButtonType,
  ButtonSize,
  AlertToastType,
  ToastController,
  BadgeShape,
  BadgeSize,
  BadgeColor,
  TooltipPosition,
  TooltipAlignment,
  AlertBannerType,
  AlertBannerVariant,
  ExIconButton,
  IconButtonType,
  IconButtonFlavor,
  IconButtonSize,
  InputFooterType,
  ComboboxSelect,
  ComboboxType,
  TargetVariant,
  LoaderSize,
  LoaderVariant,
} from '@boomi/exosphere';
import './CreateModelConfigurationDrawer.scss';
import modelsApiService, {
  type ModelConfigDetailsResponse,
  type ModelProvider,
} from '@services/agent-foundation/agent-foundation-models-api-service';
import { getEnvironments } from '@services/agent-foundation/agent-foundation-environments-api-service';
import useSideDrawerCustomIcon from '@components/ToolManagement/helpers/useSideDrawerCustomIcon';
import TooltipWithLink from '@components/TooltipWithLink/TooltipWithLink';
import noResultsImage from '@assets/boomi-ai/ui-kit/agentFoundation/No_result.svg';
import {
  CreateModelConfigurationDrawerProps,
  RegisterModelFormData,
  DrawerState,
  ProviderOption,
  EnvironmentOption,
} from './types';
import { generateUniqueKey } from '@services/agent-foundation/agent-foundation-api-service';
import modelProviderLogo from '../modelProviderLogo';

// Mock data for provider options
const PROVIDER_OPTIONS: ProviderOption[] = [
  { value: 'anthropic', label: 'Anthropic', icon: 'Anthropic' },
  { value: 'openai', label: 'OpenAI', icon: 'OpenAI' },
  { value: 'google', label: 'Google', icon: 'Gemini' },
  // { value: 'Custom', label: 'Custom' },
];

// Translation keys for provider display labels, keyed by the option's (untranslated) label
// used for provider-logo lookup / selection matching
const PROVIDER_LABEL_KEYS: Record<string, string> = {
  Anthropic: 'models_provider_anthropic',
  OpenAI: 'models_provider_openai',
  google: 'models_provider_google',
};

function CreateModelConfigurationDrawer({
  isOpen,
  onClose,
  onRegister,
  testId = 'register-model-drawer',
  showNavigation = false,
  isEditMode = false,
  editingModel = null,
  preselectedProvider = undefined,
  onCancel: handleCancel = onClose,
}: Readonly<CreateModelConfigurationDrawerProps>) {
  const [t] = useTranslation('common');
  // Keep form controls unmounted until environments have loaded. ExSelect's
  // cleanup expects its internal input to be initialized before disconnecting.
  const [isLoading, setIsLoading] = useState(true);
  const { sideDrawerRef, trailingIconPortal } = useSideDrawerCustomIcon(
    true,
    isLoading,
    false,
    false,
    true,
  );

  // Form state
  const [formData, setFormData] = useState<RegisterModelFormData>({
    modelName: '',
    provider: preselectedProvider ? (preselectedProvider as any).toLowerCase() : '',
    apiKey: '',
    models: [],
    environment: [],
  });
  useEffect(() => {
    if (preselectedProvider) {
      setFormData((prev) => ({
        ...prev,
        provider: preselectedProvider.toLowerCase() as ModelProvider,
        models: [],
        environment: [],
      }));
    }
  }, [preselectedProvider]);
  // UI state
  const [drawerState, setDrawerState] = useState<DrawerState>({
    isLoading: false,
    connectionVerified: false,
    error: null,
  });
  const [modelNameError, setModelNameError] = useState<string | undefined>(undefined);
  const [isNameTaken, setIsNameTaken] = useState(false);
  const [updatedApiKey, setUpdatedApiKey] = useState(false);
  const [environmentOptions, setEnvironmentOptions] = useState<EnvironmentOption[]>([]);
  const [hasEnvironments, setHasEnvironments] = useState(true);
  const [modelCatalog, setModelCatalog] = useState<Record<string, any[]>>({});
  const [isLoadingModels, setIsLoadingModels] = useState(false);
  const [configuredModels, setConfiguredModels] = useState<any[]>([]);
  const [modelAgentCounts, setModelAgentCounts] = useState<Record<string, number>>({});
  const [isEditDataInitialized, setIsEditDataInitialized] = useState(false);
  const [editDataError, setEditDataError] = useState<string | null>(null);
  const [initialEnvironmentEmpty, setInitialEnvironmentEmpty] = useState(false);

  // Load model catalog from API when provider is selected
  const loadModelCatalog = async () => {
    setIsLoadingModels(true);
    try {
      const models = await modelsApiService.getModelCatalog({
        provider: formData.provider.toLowerCase(),
      });
      // Store models for the selected provider
      setModelCatalog({
        [formData.provider]: models,
      });

      // New model configurations select every selectable model by default.
      // Existing configurations keep the model selections loaded from their details.
      if (!isEditMode) {
        setFormData((prev) => ({
          ...prev,
          models: models
            .filter((model: any) => {
              const status = model.status?.toLowerCase();
              return status !== 'deprecated' && status !== 'retired';
            })
            .map((model: any) => model.model_id || model.id)
            .filter(Boolean),
        }));
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to load model catalog:', error);
      setModelCatalog({});
    } finally {
      setIsLoadingModels(false);
    }
  };
  useEffect(() => {
    if (!formData.provider) {
      setModelCatalog({});
      setIsLoadingModels(false);
      return;
    }
    loadModelCatalog();
  }, [formData.provider, isEditMode]);
  const loadEnvironments = async () => {
    setIsLoading(true);
    try {
      const response: any = await getEnvironments({
        classification: 'PRODUCTION',
        has_common_runtime: true,
      });
      const environments = response?.data?.items || response?.data || response || [];
      const envArray = Array.isArray(environments) ? environments : [];

      if (envArray.length === 0) {
        setHasEnvironments(false);
        setEnvironmentOptions([]);
      } else {
        setHasEnvironments(true);
        const options = envArray.map((env: any) => ({
          value: env.id || env.ag_environment_id || env.integration_environment_id || '',
          label: env.name || env.environment_name || '',
        }));
        setEnvironmentOptions(options);

        // New model configurations apply to every available environment by default.
        // Existing configurations keep their environments from the model details.
        if (!isEditMode) {
          setFormData((prev) => ({
            ...prev,
            environment: options.map((option) => option.value),
          }));
        }
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to load environments:', error);
      setHasEnvironments(false);
      setEnvironmentOptions([]);
    } finally {
      setIsLoading(false);
    }
  };
  // Load environments with filters: classification=PRODUCTION and has_common_runtime=true
  useEffect(() => {
    loadEnvironments();
  }, [isEditMode]);

  useEffect(() => {
    if (!isEditMode || !editingModel) {
      setIsEditDataInitialized(false);
      return;
    }

    const loadEditData = async () => {
      setIsEditDataInitialized(false);
      setEditDataError(null);
      try {
        const configId = editingModel.id || editingModel.model_id;
        const details = await modelsApiService.getModelConfigDetails(configId || '');
        const modelConfig = details?.data as ModelConfigDetailsResponse;
        const environmentNames = modelConfig.environments?.map(
          (env: any) => env.environment_id,
        ) || [];
        setInitialEnvironmentEmpty(environmentNames.length === 0);
        const configuredModelRecords = modelConfig.models || [];
        const modelIds = configuredModelRecords.map((model: any) => model.model_id || model.id);
        const agentCounts: Record<string, number> = {};
        configuredModelRecords.forEach((model: any) => {
          const modelId = model.model_id || model.id;
          if (modelId) agentCounts[modelId] = model.deployment_count || 0;
        });
        setConfiguredModels(configuredModelRecords);
        setModelAgentCounts(agentCounts);
        setFormData({
          modelName: modelConfig.name,
          provider: modelConfig.provider as ModelProvider,
          apiKey: '', // Don't show the actual API key for security
          models: modelIds,
          environment: environmentNames,
        });
        setDrawerState((prev) => ({
          ...prev,
          connectionVerified: true,
        }));
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : t('models_error_update_failed');
        setEditDataError(errorMessage);
        setDrawerState((prev) => ({ ...prev, error: errorMessage }));
      } finally {
        setIsEditDataInitialized(true);
      }
    };

    loadEditData();
  }, [isEditMode, editingModel]);
  // Reset form when drawer closes
  const handleClose = useCallback(() => {
    setFormData({
      modelName: '',
      provider: '',
      apiKey: '',
      models: [],
      environment: [],
    });
    setDrawerState({
      isLoading: false,
      connectionVerified: false,
      error: null,
    });
    setUpdatedApiKey(false);
    setConfiguredModels([]);
    setModelAgentCounts({});
    setIsEditDataInitialized(false);
    setEditDataError(null);
    setInitialEnvironmentEmpty(false);
    onClose();
  }, [onClose]);

  // Handle form field changes
  const handleFieldChange = useCallback(
    (field: keyof RegisterModelFormData, value: string | string[]) => {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));

      // Clear error when user starts typing
      if (drawerState.error) {
        setDrawerState((prev) => ({
          ...prev,
          error: null,
        }));
      }

      // Clear the name conflict error as soon as the user edits the name
      if (field === 'modelName') {
        setModelNameError(undefined);
        if (isNameTaken) {
          setIsNameTaken(false);
        }
      }

      // Mark connection as verified when provider is selected
      if (field === 'provider' && value) {
        setDrawerState((prev) => ({
          ...prev,
          connectionVerified: true,
        }));
      }
      // Clear provider-specific fields when provider changes
      if (field === 'provider') {
        setFormData((prev) => ({
          ...prev,
          apiKey: '',
          models: [],
          environment: isEditMode
            ? []
            : environmentOptions.map((option) => option.value),
        }));
        setUpdatedApiKey(false);
      }
    },
    [drawerState.error, environmentOptions, isEditMode, isNameTaken],
  );

  // Toggle model selection
  const handleModelToggle = useCallback(
    (modelValue: string) => {
      setFormData((prev) => ({
        ...prev,
        models: prev.models.includes(modelValue)
          ? prev.models.filter((m) => m !== modelValue)
          : [...prev.models, modelValue],
      }));
    },
    [],
  );

  // An API key may be left unchanged in edit mode, but a cleared replacement is invalid.
  const isApiKeyValid = isEditMode
    ? (!updatedApiKey || Boolean(formData.apiKey))
    : Boolean(formData.apiKey);

  // Submit form
  const handleRegister = async () => {
    if (
      !formData.modelName
      || !formData.provider
      || !isApiKeyValid
      || formData.models.length === 0
      || formData.environment.length === 0
    ) {
      setDrawerState((prev) => ({
        ...prev,
        error: t('models_error_fill_all_fields'),
      }));
      return;
    }

    setDrawerState((prev) => ({
      ...prev,
      isLoading: true,
      error: null,
    }));

    try {
      const result = await onRegister(formData);
      if (result && result.success === false) {
        const errorText = result.error || result.message || '';
        if (typeof errorText === 'string' && errorText.includes('already exists in this account')) {
          setModelNameError(t('models_error_name_exists', { name: formData.modelName }));
          setIsNameTaken(true);
        }
        setDrawerState((prev) => ({
          ...prev,
          isLoading: false,
        }));
        return;
      }
      handleClose();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : t('models_error_registration_failed');
      setDrawerState((prev) => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
      ToastController.show({
        description: errorMessage,
        type: AlertToastType.ERROR,
      });
    }
  };

  // Helper function to determine if API key should show placeholder
  const shouldShowApiKeyPlaceholder = (): boolean => (
    isEditMode && !updatedApiKey && editingModel?.id !== undefined
  );

  const availableModels = formData.provider
    ? (() => {
      const catalogModels = modelCatalog[formData.provider] || [];
      const catalogModelIds = new Set(
        catalogModels.map((model: any) => model.model_id || model.id),
      );
      const linkedModelsMissingFromCatalog = configuredModels.filter(
        (model: any) => {
          const modelId = model.model_id || model.id;
          return modelId && !catalogModelIds.has(modelId);
        },
      );

      return [...catalogModels, ...linkedModelsMissingFromCatalog].filter(
        (model: any) => {
          const modelId = model.model_id || model.id;
          const status = model.status?.toLowerCase();
          return (status !== 'deprecated' && status !== 'retired')
            || modelAgentCounts[modelId] > 0;
        },
      );
    })()
    : [];
  // A deprecated model with deployed agents can't be removed from the connection
  const hasDeprecatedDisabledModel = availableModels.some(
    (option: any) => modelAgentCounts[option.model_id] > 0
      && option.status?.toLowerCase() === 'deprecated',
  );
  // A retired model with deployed agents can't be removed from the connection
  const hasRetiredDisabledModel = availableModels.some(
    (option: any) => modelAgentCounts[option.model_id] > 0
      && option.status?.toLowerCase() === 'retired',
  );
  // Check if form is valid for submission
  const isFormValid = formData.modelName
    && formData.provider
    && isApiKeyValid
    && formData.models.length > 0
    && formData.environment.length > 0;

  const getRuntimeManagementUrl = () => {
    const domain = window.location.origin;
    return `${domain}/orchestrate/runtimes`;
  };
  const renderContent = () => {
    if (isLoading || drawerState.isLoading || (isEditMode && !isEditDataInitialized)) {
      return (
        <div className="register-model-drawer__loader" data-testid="create-model-config--loader">
          <ExLoader
            size={LoaderSize.MEDIUM}
            variant={LoaderVariant.SPINNER}
            label={t('ai_loading', 'Loading')}
          />
        </div>
      );
    }

    if (editDataError) {
      return (
        <ExAlertBanner
          type={AlertBannerType.ERROR}
          open
          variant={AlertBannerVariant.SUBTLE}
          data-testid={`${testId}-edit-data-error`}
        >
          {editDataError}
        </ExAlertBanner>
      );
    }

    if (!hasEnvironments) {
      return (
        <div className="register-model-drawer__empty-state-container">
          <ExEmptyState
            label={t('no_self_hosted_runtime_title')}
            text={t('no_self_hosted_runtime_description')}
            data-testid="no-self-hosted-runtime-state"
            linkLabel={t('runtime_management')}
            linkTarget={TargetVariant.BLANK}
            link={getRuntimeManagementUrl()}
          >
            <div
              slot="header"
              style={{
                marginBottom: '20px',
              }}
            >
              <img
                alt="No Self-hosted Runtime"
                src={noResultsImage}
                style={{
                  width: '150px',
                  height: '150px',
                }}
              />
            </div>
          </ExEmptyState>
        </div>
      );
    }
    function mapProviderFromResponse(provider: any): string {
      switch (provider) {
        case 'anthropic':
          return t('models_provider_anthropic');
        case 'gemini':
          return t('models_provider_gemini');
        case 'google':
          return t('models_provider_google');
        case 'openai':
          return t('models_provider_openai');
        default:
          return 'None';
      }
    }
    return (
      <div className="register-model-drawer__form" style={{ position: 'relative' }}>
        {/* Model Name Field */}
        <span className="register-model-drawer__info-text">
          {t('models_drawer_subtitle')}
        </span>

        <div className="register-model-drawer__form-group">
          <ExSelect
            id="provider"
            selected={mapProviderFromResponse(formData.provider)}
            onChange={(e: any) => {
              const selectedValue = e.target.value || e.detail?.value;
              const normalizedSelectedValue = typeof selectedValue === 'string'
                ? selectedValue.toLowerCase()
                : selectedValue;
              const currentProvider = formData.provider?.toLowerCase();
              if (normalizedSelectedValue && normalizedSelectedValue !== currentProvider) {
                handleFieldChange('provider', selectedValue);
              }
            }}
            label={t('models_field_provider')}
            hideClearIcon
            data-testid={`${testId}-provider`}
            className="register-model-drawer__select"
            required
            disabled={isEditMode}
            showSelectedLabel
          >
            {PROVIDER_OPTIONS.map((option, index) => {
              const logo = modelProviderLogo(option.label as any);
              return (
                <ExMenuItem key={generateUniqueKey(index)} value={option.value}>
                  <div className="register-model-drawer__provider-option">
                    <img
                      src={logo.src}
                      alt={logo.alt}
                    />
                  </div>
                  <span>{t(PROVIDER_LABEL_KEYS[option.label] || option.label)}</span>
                </ExMenuItem>
              );
            })}
          </ExSelect>
        </div>
        {(isEditMode || formData.provider) && (
          <div className="register-model-drawer__form-group">
            <ExInput
              id="model-name"
              type="text"
              label={t('models_field_model_name')}
              infoText={t('models_field_model_name_info')}
              placeholder={t('models_field_model_name_placeholder')}
              value={formData.modelName}
              onChange={(e: any) => handleFieldChange('modelName', e.target.value)}
              onInput={(e: any) => handleFieldChange('modelName', e.target.value)}
              data-testid={`${testId}-model-name`}
              className="register-model-drawer__input"
              maxlength={100}
              required
              hideCharCount
              invalid={!!modelNameError}
              errorMsg={modelNameError}
              autoComplete="one-time-code"
              disabled={isEditMode}
            />
          </div>
        )}
        {/* API Key Field - Only show after provider is selected */}
        {formData.provider && (
          <div className="register-model-drawer__form-group">
            <ExInput
              id="api-key"
              label={t('models_field_api_key')}
              type="password"
              placeholder={shouldShowApiKeyPlaceholder() ? t('hidden') : t('models_field_api_key_placeholder')}
              value={formData.apiKey}
              onChange={(e: any) => {
                handleFieldChange('apiKey', e.target.value);
                setUpdatedApiKey(true);
              }}
              onInput={(e: any) => {
                handleFieldChange('apiKey', e.target.value);
                setUpdatedApiKey(true);
              }}
              data-testid={`${testId}-api-key`}
              className="register-model-drawer__input"
              maxlength={2000}
              helpText={t('models_api_key_help_text')}
              footerType={InputFooterType.INFO}
              showStatusIcon
              required
              hideCharCount
              togglePassword
              suppressValidityCheck={shouldShowApiKeyPlaceholder()}
            />
          </div>
        )}
        {/* Environment Field - Only show after provider is selected */}
        {formData.provider && environmentOptions && (
          <div className="register-model-drawer__form-group">
            <ExCombobox
              id="environment"
              label={t('models_field_environment')}
              placeholder={t('models_field_environment_placeholder')}
              strategy="absolute"
              type={ComboboxType.STANDARD}
              autoComplete="off"
              selectAllMenuItem
              showClear
              disabled={isEditMode && !initialEnvironmentEmpty}
              {...(formData.environment.length > 0 && {
                defaultValues: Array.from(new Set(formData.environment)).map((env) => {
                  const matchedOption = environmentOptions.find((opt) => opt.value === env);
                  return {
                    value: env,
                    label: matchedOption?.label || env,
                  };
                }),
              })}
              onChange={(e: any) => {
                e.preventDefault();
                const selectedValues = e.detail.data || e.detail.values || [];
                const envValues = Array.from(
                  new Set(
                    selectedValues.map((item: any) => item.value || item),
                  ),
                ) as string[];
                handleFieldChange('environment', envValues);
              }}
              onRemove={(e: any) => {
                e.preventDefault();
                const removedValue = e.detail?.option?.value || e.detail?.value;
                const updatedEnvs = formData.environment.filter(
                  (env) => env !== removedValue,
                );
                handleFieldChange('environment', updatedEnvs);
              }}
              infoText={t('models_field_model_environment_info')}
              data-testid={`${testId}-environment`}
              className="register-model-drawer__select"
              select={ComboboxSelect.MULTI}
              comboboxOptions={environmentOptions}
              required
            />
          </div>
        )}
        {/* Model Field - Card based multi-selection after provider is selected */}
        {formData.provider && (
          <div className="register-model-drawer__form-group">
            <div className="register-model-drawer__models-header">
              <div className="register-model-drawer__models-header-left">
                <label htmlFor="models-grid" className="register-model-drawer__label">
                  {t('models_field_model')}
                </label>
                <ExTooltip
                  position={TooltipPosition.BOTTOM}
                  alignment={TooltipAlignment.START}
                  trigger="hover focus"
                  keepPopupOpen
                >
                  <div>
                    {t('models_field_model_models_selection_info')}
                  </div>
                  <ExIconButton
                    slot="anchor"
                    icon="information"
                    hideBrowserTooltip
                    type={IconButtonType.TERTIARY}
                    flavor={IconButtonFlavor.BRANDED}
                    size={IconButtonSize.XSMALL}
                    label="Information"
                    className="register-model-drawer__info-icon"
                    circular
                  />
                </ExTooltip>
              </div>
              <ExBadge
                className="register-model-drawer__selection-count"
                color={formData.models.length > 0 ? BadgeColor.BLUE : BadgeColor.YELLOW}
                shape={BadgeShape.SQUARED}
                size={BadgeSize.SMALL}
                data-testid="register-model-drawer-model-selection-count"
              >
                {formData.models.length}
                /
                {availableModels.length}
                {' '}
                {t('models_field_model_selected_count_label')}
              </ExBadge>
            </div>
            {isLoadingModels || (isEditMode && !isEditDataInitialized) ? (
              <div className="register-model-drawer__loader">
                <ExLoader
                  size={LoaderSize.MEDIUM}
                  variant={LoaderVariant.SPINNER}
                  label={t('ai_loading', 'Loading')}
                />
              </div>
            ) : (
              <>
                {hasDeprecatedDisabledModel && (
                  <ExAlertBanner
                    type={AlertBannerType.WARNING}
                    open
                    variant={AlertBannerVariant.SUBTLE}
                    className="register-model-drawer__warning-banner"
                    data-testid={`${testId}-deprecated-model-warning`}
                  >
                    {t('models_deprecated_model_banner')}
                  </ExAlertBanner>
                )}
                {hasRetiredDisabledModel && (
                  <ExAlertBanner
                    type={AlertBannerType.ERROR}
                    open
                    variant={AlertBannerVariant.SUBTLE}
                    className="register-model-drawer__warning-banner"
                    data-testid={`${testId}-retired-model-warning`}
                  >
                    {t('models_retired_model_banner')}
                  </ExAlertBanner>
                )}
                {formData.models.length === 0 && (
                  <ExAlertBanner
                    type={AlertBannerType.WARNING}
                    open
                    variant={AlertBannerVariant.SUBTLE}
                    className="register-model-drawer__warning-banner"
                    data-testid={`${testId}-models-warning`}
                  >
                    {t('models_error_at_least_one_required')}
                  </ExAlertBanner>
                )}
                <div id="models-grid" className="register-model-drawer__models-grid">
                  {availableModels.map((option: any) => {
                    const modelId = option.model_id || option.id;
                    const isModelCheckboxDisabled = modelAgentCounts[modelId] > 0;
                    return (
                      <ExCard
                        key={modelId}
                        className={`register-model-drawer__model-card ${
                          formData.models.includes(modelId)
                            ? 'register-model-drawer__model-card--selected'
                            : ''
                        }`}
                        data-testid={`${testId}-model-${modelId}`}
                        onClick={() => {
                          if (!isModelCheckboxDisabled) handleModelToggle(modelId);
                        }}
                      >
                        <div className="register-model-drawer__model-card-content">
                          <div className="register-model-drawer__model-card-title">
                            {isModelCheckboxDisabled ? (
                              <ExTooltip
                                position={TooltipPosition.TOP}
                                alignment={TooltipAlignment.MIDDLE}
                              >
                                <div>
                                  {t('models_checkbox_disabled_tooltip')}
                                </div>
                                <ExCheckbox
                                  slot="anchor"
                                  id={`model-${modelId}`}
                                  checked={formData.models.includes(modelId)}
                                  onChange={() => handleModelToggle(modelId)}
                                  className="register-model-drawer__model-checkbox"
                                  data-testid={`checkbox-model-${modelId}`}
                                  disabled
                                />
                              </ExTooltip>
                            ) : (
                              <ExCheckbox
                                id={`model-${modelId}`}
                                checked={formData.models.includes(modelId)}
                                onChange={() => handleModelToggle(modelId)}
                                data-testid={`checkbox-model-${modelId}`}
                                className="register-model-drawer__model-checkbox"
                              />
                            )}
                            <span className="register-model-drawer__model-title">
                              {option.label || option.display_name}
                            </span>
                            {(option.status?.toLowerCase() === 'deprecated'
                              || option.status?.toLowerCase() === 'retired') && (
                              <ExBadge
                                className="register-model-drawer__status-badge"
                                color={
                                  option.status?.toLowerCase() === 'deprecated'
                                    ? BadgeColor.YELLOW
                                    : BadgeColor.RED
                                }
                                shape={BadgeShape.SQUARED}
                                size={BadgeSize.SMALL}
                                data-testid={`${testId}-model-${modelId}-status-badge`}
                              >
                                {t(
                                  option.status?.toLowerCase() === 'deprecated'
                                    ? 'models_badge_deprecated'
                                    : 'models_badge_retired',
                                )}
                              </ExBadge>
                            )}
                          </div>
                          <div className="register-model-drawer__model-content">
                            {option.description && (
                              <div className="register-model-drawer__model-description-wrapper">
                                <div className="register-model-drawer__model-description-label">
                                  {t('description')}
                                </div>
                                <ExTooltip
                                  position={TooltipPosition.TOP}
                                  alignment={TooltipAlignment.MIDDLE}
                                >
                                  <div>
                                    {option.description}
                                  </div>
                                  <div slot="anchor" className="register-model-drawer__model-description-long" data-testid="register-model-drawer-model-description-long">
                                    {option.description}
                                  </div>
                                </ExTooltip>
                              </div>
                            )}
                            {isEditMode && (
                              <div className="register-model-drawer__model-description-wrapper">
                                <div className="register-model-drawer__model-description-label">
                                  {t('models_table_column_agents')}
                                </div>
                                <div className="register-model-drawer__model-description" data-testid="register-model-drawer-model-agent-count">
                                  {modelAgentCounts[modelId] ?? option.deployment_count ?? 0}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </ExCard>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="register-model-drawer-wrapper">
      <ExSideDrawer
        ref={sideDrawerRef as any}
        open={isOpen}
        onClose={handleClose}
        panelTitle={t(isEditMode ? 'models_drawer_title_edit' : 'models_drawer_title')}
        className={`register-model-drawer${isEditMode ? ' register-model-drawer--edit' : ''}`}
        data-testid={testId}
        navigation={showNavigation}
        preventStacking={!showNavigation}
        onCancel={handleCancel}
      >
        {trailingIconPortal && ReactDOM.createPortal(
          <TooltipWithLink
            infoTextKey="models_drawer_description"
            linkTextKey="create_model_connection_link"
            testId="create-model-connection-help-link"
            iconButtonTestId="create-model-connection-tooltip-trigger"
          />,
          trailingIconPortal,
        )}
        {renderContent()}
        {hasEnvironments && (
          <div
            className={`register-model-drawer__footer${isLoading ? ' register-model-drawer__footer--hidden' : ''}`}
            slot="footer"
          >
            <ExButton
              type={ButtonType.SECONDARY}
              flavor={ButtonFlavor.BRANDED}
              size={ButtonSize.DEFAULT}
              onClick={handleClose}
              data-testid={`${testId}-close`}
            >
              {t('models_button_close')}
            </ExButton>
            <ExButton
              type={ButtonType.PRIMARY}
              flavor={ButtonFlavor.BRANDED}
              size={ButtonSize.DEFAULT}
              onClick={handleRegister}
              disabled={!isFormValid || isLoading || drawerState.isLoading || isNameTaken}
              data-testid={`${testId}-save`}
            >
              {t('models_button_save')}
            </ExButton>
          </div>
        )}
      </ExSideDrawer>
    </div>
  );
}

export default CreateModelConfigurationDrawer;
